import React, { createContext, createRef, useCallback, useEffect, useImperativeHandle, useState, useMemo } from 'react';
import { Typography, ButtonBase, Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { AdmIcon, SettingIcon } from '../../components/Icon';
import { i18n } from '../../language';
import rou from '../../router';
import { classNames } from '../../utils';
import { authNameE } from '../../utils/userFuncs';
import { ReactComponent as RedFYA } from '../../static/images/RedFYA.svg';
import { ReactComponent as PWR } from '../../static/images/PWR.svg';
import { bedStatusAPI } from '../../apis';
import { BEDSTATUS_OUTSTANDING_TRANSFER_IN } from '../../comm/constant';
import { playAudio, useActionableSettings } from '../../utils/hooks';
import { BsAlertSrc } from '../../utils/bsAlertUtil';
import { userRoleFunc } from '../../utils/userRole';
import { BedStatusAll, InternalBooking, TransferOut } from './components';
import { baseCssBedStatus } from './components/baseCss/baseCssBedStatus';
import Admission, { bsAdmissionRef } from './components/InternalBooking/Admission';
import { bsTransferInRef } from './components/InternalBooking';

const useStyles = makeStyles((theme) => ({
  root: ({ sso }) => ({
    position: 'fixed',
    top: sso ? '0' : '4.375rem',
    left: sso ? '0' : '6.25rem',
    bottom: '0',
    right: '0',
    display: 'flex',
    flexDirection: 'column',
  }),
  versionBtn: ({ fullScreen }) => ({
    color: '#7d86a9',
    fontSize: '0.875rem',
    fontFamily: 'NotoSans-Bold',
    padding: '0 0.969rem',
    border: fullScreen ? 'unset' : 'solid 1px #e4e7eb',
    borderRadius: '8px',
    height: fullScreen ? '2.625rem' : '2.938rem',
    backgroundColor: '#fff',
  }),
  icon: {
    width: '1.875rem',
    height: '1.875rem',
  },
  value: {
    fontSize: '1.5rem',
    color: '#2E4469',
    fontWeight: 'NotoSans-Bold',
  },
  tabButton: {
    minWidth: '10rem',
    height: '3.875rem',
    backgroundColor: '#538fff11',
    borderRadius: '0.75rem 0.75rem 0 0',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#538fff88',
    padding: '0 1rem',
    gap: '1rem',
  },
}));

export const bedstatusRef = createRef();

export const bsTransferInStatisticRef = createRef();

export default function BedStatus(props) {
  const sso = useSelector((state) => state.ssoReducer);
  const baseClasses = baseCssBedStatus();
  const classes = useStyles({ sso });
  const roleMap = useSelector((state) => state.roleMap);
  const language = useSelector((state) => state.settings.language);
  const statisticStorage = JSON.parse(sessionStorage.getItem(BEDSTATUS_STATISTIC));
  const [statistic, setStatistic] = useState({
    pendingWardAckCount: statisticStorage?.pendingWardAckCount ?? 0,
    outstandingCount: statisticStorage?.outstandingCount ?? 0,
    eAdmOutstandingCount: statisticStorage?.eAdmOutstandingCount ?? 0,
    pendingFYACount: statisticStorage?.pendingFYACount ?? 0,
    pendingWardReadyCount: statisticStorage?.pendingWardReadyCount ?? 0,
  });
  const loginInfo = useSelector((state) => state.loginInfo);
  const AdmissionWorkflowReducer = useSelector((state) => state.AdmissionWorkflowReducer);
  const [actionableSetting] = useActionableSettings();
  const [refresh, setRefresh] = useState(true)

  useEffect(() => {
    if (!actionableSetting.mute && !AdmissionWorkflowReducer && statistic.pendingFYACount > 0) {
      playAudio(BsAlertSrc);
      const interval = setInterval(() => {
        playAudio(BsAlertSrc);
      }, 1000 * 20);

      return () => clearInterval(interval);
    }
  }, [actionableSetting.mute, AdmissionWorkflowReducer, statistic.pendingFYACount]);

  const [TabIndex, setTabIndex] = useState(
    sessionStorage.getItem('bsType') ? parseInt(sessionStorage.getItem('bsType')) : 0
  );
  const container = [
    { id: 0, component: BedStatusAll, idName: 'bed_status_tab1' },
    { id: 1, component: InternalBooking, idName: 'bed_status_tab2' },
    { id: 2, component: TransferOut, idName: 'bed_status_tab3' },
    { id: 3, component: Admission, idName: 'bed_status_tab4' },
  ];
  const tabHandler = (position) => {
    return () => {
      setTabIndex(position);
      sessionStorage.setItem('bsType', position);
    };
  };

  const showContainer = () => {
    let index = container.findIndex((item, index) => {
      return index === TabIndex;
    });
    let Component = index > -1 ? container[index].component : null;
    Component = Component ?? container[0].component;
    return (
      <Component
        language={language}
        tabHandler={(index) => {
          tabHandler(index)();
        }}
      />
    );
  };

  const skipSeeting = () => {
    props.history.push(`/newBedstatus/setting/plain`);
  };

  const handleLatestVersion = useCallback(() => {
    props.history.push(rou.bedstatusNewRou.path);
  }, []);

  const getTransferStatistic = async () => {
    try {
      const { result } = await bedStatusAPI.getTransferStatistic();
      setStatistic(result);
      sessionStorage.setItem(BEDSTATUS_STATISTIC, JSON.stringify(result));
    } catch (error) {
      console.log(error);
    }
  };

  useImperativeHandle(bsTransferInStatisticRef, () => ({
    getTransferStatistic,
  }));

  useEffect(() => {
    // 如果在当前页面切换了用户或者角色，且没有权限访问admission，则显示回bed status的tab
    const hasAdmissionPermission = userRoleFunc?.getIsShowAdmission();
    if (!hasAdmissionPermission && [1, 2, 3].includes(TabIndex)) {
      tabHandler(0)();
    }

    getTransferStatistic();
  }, [loginInfo]);

  const handleCountButton = (tabValue, status) => (event) => {
    event.stopPropagation();
    if (TabIndex === tabValue) {
      if (tabValue === 1) {
        bsTransferInRef?.current?.selectTab?.(status);
      } else if (tabValue === 3) {
        bsAdmissionRef?.current?.selectTab?.(status);
      }
    } else {
      tabHandler(tabValue)();
      const startTime = moment()
        .subtract(tabValue === 1 ? 7 : 1, 'days')
        .format('yyyy-MM-DD 00:00:00');
      const stopTime = moment().add(1, 'days').format('yyyy-MM-DD 23:59:59');
      if (_.some(status, (item) => item === 'O')) {
        sessionStorage.setItem(
          BEDSTATUS_OUTSTANDING_TRANSFER_IN,
          JSON.stringify({
            startTime: startTime,
            stopTime: stopTime,
            status: status,
            noDate: false,
          })
        );
      } else {
        sessionStorage.setItem(
          BEDSTATUS_OUTSTANDING_TRANSFER_IN,
          JSON.stringify({
            startTime: null,
            stopTime: null,
            status: status,
            noDate: true,
          })
        );
      }
    }
  };

  const statisticValue = useMemo(() => ({ statistic,refresh, setRefresh }), [statistic,refresh, setRefresh]);

  return (
    <BedStatusStatisticContext.Provider value={statisticValue}>
      <div className={classes.root}>
        <Box display="flex" justifyContent="space-between" margin={sso ? '0' : '1.625rem 3.438rem 0'}>
          <Box display="flex" gridGap="0.625rem">
            <ButtonBase
              id="bed_status_tab1"
              className={0 === TabIndex ? classNames(classes.tabButton, baseClasses.selectBtn) : classes.tabButton}
              onClick={tabHandler(0)}
            >
              {i18n.t('BEDSTATUS_BED_STATUS')}
            </ButtonBase>

            {userRoleFunc?.getIsShowAdmission() && (
              <>
                {AdmissionWorkflowReducer && (
                  <ButtonBase
                    id="bed_status_tab4"
                    className={
                      3 === TabIndex ? classNames(classes.tabButton, baseClasses.selectBtn) : classes.tabButton
                    }
                    onClick={tabHandler(3)}
                  >
                    E-Adm.
                    <ButtonBase onClick={handleCountButton(3, ['PWA'])}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gridGap="0.25rem"
                        p="0.5rem"
                        bgcolor={statistic.pendingWardAckCount > 0 ? '#fff3bb' : null}
                      >
                        <RedFYA className={classes.icon} />
                        <div className={classes.value}>{statistic.pendingWardAckCount}</div>
                      </Box>
                    </ButtonBase>
                    <ButtonBase onClick={handleCountButton(3, ['PWR'])}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gridGap="0.25rem"
                        p="0.5rem"
                        bgcolor={statistic.pendingWardReadyCount > 0 ? '#fff3bb' : null}
                      >
                        <PWR className={classes.icon} />
                        <div className={classes.value}>{statistic.pendingWardReadyCount}</div>
                      </Box>
                    </ButtonBase>
                    <ButtonBase onClick={handleCountButton(3, ['O'])}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gridGap="0.25rem"
                        p="0.5rem"
                        bgcolor={statistic.eAdmOutstandingCount > 0 ? '#fff3bb' : null}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="1.875rem"
                          height="1.875rem"
                          bgcolor="#00A8F9"
                          borderRadius="5px"
                        >
                          <AdmIcon style={{ fontSize: '1rem', color: '#FFF' }} />
                        </Box>
                        <div className={classes.value}>{statistic.eAdmOutstandingCount}</div>
                      </Box>
                    </ButtonBase>
                  </ButtonBase>
                )}
                <ButtonBase
                  id="bed_status_tab2"
                  className={1 === TabIndex ? classNames(classes.tabButton, baseClasses.selectBtn) : classes.tabButton}
                  onClick={tabHandler(1)}
                >
                  T/I Req.
                  <ButtonBase onClick={handleCountButton(1, ['PFYA'])}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap="0.25rem"
                      p="0.5rem"
                      bgcolor={statistic.pendingFYACount > 0 ? '#fff3bb' : null}
                    >
                      <RedFYA className={classes.icon} />
                      <div className={classes.value}>{statistic.pendingFYACount}</div>
                    </Box>
                  </ButtonBase>
                  <ButtonBase onClick={handleCountButton(1, ['O', 'WR'])}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap="0.25rem"
                      p="0.5rem"
                      bgcolor={statistic.outstandingCount > 0 ? '#fff3bb' : null}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width="1.875rem"
                        height="1.875rem"
                        bgcolor="#00A8F9"
                        borderRadius="5px"
                      >
                        <AdmIcon style={{ fontSize: '1rem', color: '#FFF' }} />
                      </Box>
                      <div className={classes.value}>{statistic.outstandingCount}</div>
                    </Box>
                  </ButtonBase>
                </ButtonBase>
                <ButtonBase
                  id="bed_status_tab3"
                  className={2 === TabIndex ? classNames(classes.tabButton, baseClasses.selectBtn) : classes.tabButton}
                  onClick={tabHandler(2)}
                >
                  {i18n.t('BEDSTATUS_TRANSFER_OUT')}
                  {/* <ButtonBase onClick={handleCountButton(2, ['P', 'A', 'DL'])}>
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap="0.25rem"
                  p="0.5rem"
                  bgcolor={statistic.pendingCount > 0 ? '#fff3bb' : '#FFF'}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="1.875rem"
                    height="1.875rem"
                    bgcolor="rgb(153,123,255)"
                    borderRadius="5px"
                  >
                    <AdmIcon style={{ fontSize: '1rem', color: '#FFF' }} />
                  </Box>
                  <div className={classes.value}>{statistic.pendingCount}</div>
                </Box>
              </ButtonBase> */}
                </ButtonBase>
              </>
            )}
          </Box>
          <Box display="flex" gridGap="1rem" alignItems="center">
            {!sso && (
              <ButtonBase className={classes.versionBtn} onClick={handleLatestVersion}>
                Cubicle View
              </ButtonBase>
            )}
            {roleMap?.[authNameE.bedStatus_edit_bed_status_module_setting] ? (
              <ButtonBase className={baseClasses.setting} onClick={skipSeeting} id="bed_setting_btn1">
                <SettingIcon style={{ fontSize: '1.25rem' }} />
                <Typography style={{ paddingLeft: '0.625rem' }}>{i18n.t('BEDSTATUS_SETTING')}</Typography>
              </ButtonBase>
            ) : (
              ''
            )}
          </Box>
        </Box>
        {showContainer()}
      </div>
    </BedStatusStatisticContext.Provider>
  );
}

export const BedStatusStatisticContext = createContext('');

export const BEDSTATUS_STATISTIC = 'BEDSTATUS_STATISTIC';
